import React, { useEffect, useMemo, useRef, useState } from "react";
import { formValueSelector, reduxForm } from "redux-form";
import { CircularProgress } from "@mui/material";
import { Collapse, Icon, notification } from "antd";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { saveJobSwmsEmergencyProcedures } from "../../../../../../../actions/v2/JobSwmsEmergencyProceduresActions";
import { Strings } from "../../../../../../../dataProvider/localize";
import jobStyles from "../../../../JobStyle.module.css";
import styles from "../pre-start/JobSwmsFormV3PreStart.module.css";
import planStyles from "../emergency-plans/JobSwmsV3EmergencyPlansContainer.module.css";
import procedureStyles from "./JobSwmsV3EmergencyProceduresContainer.module.css";
import { normalizeEmergencyProcedures, serializeEmergencyProcedures } from "./emergencyProcedureAdapter";
import JobSwmsV3EmergencyProcedureSectionRegistry from "./editor/JobSwmsV3EmergencyProcedureSectionRegistry";
import { buildEmergencyProceduresEditItems, createSwmsEditContext } from "../editHistory/swmsEditContext";

const FORM_NAME = "JobSwmsV3EmergencyProceduresContainerForm";
const selector = formValueSelector(FORM_NAME);
const getPlanKey = (plan = {}) => (
  plan.id !== undefined && plan.id !== null
    ? plan.id
    : (plan.document_number || plan.plan_id)
);

const getProcedureKey = (procedure = {}) => (
  procedure.id !== undefined && procedure.id !== null
    ? procedure.id
    : procedure.procedure_id
);

const getLinkedPlan = (procedure, planId) => (
  Array.isArray(procedure.linked_documents)
    ? procedure.linked_documents.find((linkedPlan) => `${linkedPlan.plan_id}` === `${planId}`)
    : null
);

function JobSwmsV3EmergencyProceduresContainer({
  serverState = {},
  plans = [],
  swmsData = {},
  onRetry,
  handleSubmit,
  jobId,
  swmsId,
  onClose,
  refreshForm
  ,saveRequest = 0
  ,discardRequest = 0
  ,reset
  ,pristine
  ,onDirtyChange
  ,onSavingChange
}) {
  const dispatch = useDispatch();
  const formProcedures = useSelector((state) => selector(state, "procedures"));
  const items = normalizeEmergencyProcedures(
    Array.isArray(formProcedures) ? formProcedures : serverState.data
  );
  const error = serverState.error;
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [saving, setSaving] = useState(false);
  const lastDiscardRequest = useRef(0);
  useEffect(() => {
    if (saveRequest) handleSubmit(onSubmit)();
  }, [saveRequest]);
  useEffect(() => {
    if (discardRequest && discardRequest !== lastDiscardRequest.current) {
      lastDiscardRequest.current = discardRequest;
      reset();
    }
  }, [discardRequest, reset]);
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(!pristine);
  }, [pristine, onDirtyChange]);

  const availablePlans = useMemo(
    () => (Array.isArray(plans) ? plans : []).filter((plan) => getPlanKey(plan) !== undefined),
    [plans]
  );

  useEffect(() => {
    if (
      availablePlans.length > 0
      && !availablePlans.some((plan) => `${getPlanKey(plan)}` === `${selectedPlanId}`)
    ) {
      setSelectedPlanId(getPlanKey(availablePlans[0]));
    }
  }, [availablePlans, selectedPlanId]);

  const selectedPlan = availablePlans.find(
    (plan) => `${getPlanKey(plan)}` === `${selectedPlanId}`
  ) || availablePlans[0];

  const selectedPlanProcedures = useMemo(() => {
    if (!selectedPlan) return [];

    return items
      .map((procedure) => ({
        procedure,
        linkedPlan: getLinkedPlan(procedure, getPlanKey(selectedPlan))
      }))
      .filter(({ linkedPlan }) => linkedPlan)
      .sort((left, right) => (
        Number(left.linkedPlan.display_order || 0) - Number(right.linkedPlan.display_order || 0)
      ));
  }, [items, selectedPlan]);

  const onSubmit = (values) => {
    if (saving) return;

    const procedures = serializeEmergencyProcedures(
      Array.isArray(values.procedures) ? values.procedures : items,
      Array.isArray(serverState.data) ? serverState.data : []
    );
    const editContext = createSwmsEditContext({
      initialValues: { procedures: serverState.data || [] },
      submittedValues: { procedures },
      internalRevisionId: Number.isInteger(Number(serverState.internal_revision_id))
        ? Number(serverState.internal_revision_id)
        : 0,
      documentName: "Emergency Procedures",
      documentType: "emergency_procedures",
      items: buildEmergencyProceduresEditItems(serverState.data || [], procedures)
    });
    setSaving(true);
    if (onSavingChange) onSavingChange(true);
    dispatch(saveJobSwmsEmergencyProcedures(jobId, swmsId, procedures, serverState.version, undefined, editContext))
      .then((response) => {
        notification.success({
          message: Strings.success_title,
          description: response && response.message
            ? response.message
            : "Emergency Procedures saved successfully.",
          className: "ant-success"
        });
        return refreshForm ? refreshForm({ closeOnError: false, showBlockingLoader: false }) : null;
      })
      .then(() => {
        setSaving(false);
        if (onSavingChange) onSavingChange(false);
      })
      .catch((err) => {
        setSaving(false);
        if (onSavingChange) onSavingChange(false);
        notification.error({
          message: Strings.error_title,
          description: err || Strings.generic_error,
          className: "ant-error"
        });
      });
  };

  return (
    <>
      {serverState.loading ? <div className={styles.PreStartLoadingState}><CircularProgress size={18} /></div> : null}
      {!serverState.loading && error ? <div className={styles.PreStartErrorState}><div>{error}</div><button type="button" className={styles.PreStartRetryButton} onClick={onRetry}>Retry</button></div> : null}
      {!serverState.loading && !error && items.length === 0 ? <div className={styles.PreStartEmptyState}>No emergency procedures yet.</div> : null}
      {!serverState.loading && !error && items.length > 0 && availablePlans.length === 0 ? (
        <div className={styles.PreStartEmptyState}>No linked emergency plans found.</div>
      ) : null}
      {!serverState.loading && !error && items.length > 0 && availablePlans.length > 0 ? (
        <form className="container-fluid py-3" onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-12">
              <div className={planStyles.PlansContainer}>
                <div className={planStyles.PlanTabRow} role="tablist" aria-label="Emergency Procedure Plans">
                  {availablePlans.map((plan) => {
                    const planKey = getPlanKey(plan);
                    const isSelected = `${planKey}` === `${getPlanKey(selectedPlan)}`;
                    const planName = plan.plan_name || plan.title || plan.document_number || `Plan ${planKey}`;

                    return (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={isSelected}
                        className={`${planStyles.PlanTab} ${isSelected ? planStyles.PlanTabActive : ""}`.trim()}
                        key={planKey}
                        onClick={() => setSelectedPlanId(planKey)}
                      >
                        <span className={planStyles.PlanName}>{planName}</span>
                      </button>
                    );
                  })}
                </div>
                <div className={procedureStyles.ProcedureList}>
                  <Collapse
                    key={getPlanKey(selectedPlan)}
                    accordion
                    bordered={false}
                    defaultActiveKey={selectedPlanProcedures.length > 0
                      ? `${getProcedureKey(selectedPlanProcedures[0].procedure)}`
                      : undefined}
                    expandIconPosition="right"
                    className={procedureStyles.ProcedureCollapse}
                    expandIcon={({ isActive }) => (
                      <Icon type="caret-right" rotate={isActive ? 90 : 0} />
                    )}
                  >
                    {selectedPlanProcedures.map(({ procedure, linkedPlan }) => {
                      const procedureId = getProcedureKey(procedure);
                      const isExcluded = linkedPlan.checked === false;

                      return (
                        <Collapse.Panel
                          key={`${procedureId}`}
                          className={`${procedureStyles.ProcedurePanel} ${isExcluded ? procedureStyles.ProcedurePanelExcluded : ""}`.trim()}
                          header={(
                            <div className={procedureStyles.ProcedureHeader}>
                              <span className={procedureStyles.ProcedureTitleGroup}>
                                <span className={procedureStyles.ProcedureTitle}>
                                  {procedure.title}
                                </span>
                                {isExcluded ? (
                                  <span className={procedureStyles.ProcedureExcludedLabel}>
                                    Excluded from Plan
                                  </span>
                                ) : null}
                              </span>
                              <span className={procedureStyles.ProcedureMeta}>
                                {procedure.document_number}
                              </span>
                            </div>
                          )}
                        >
                          <div className={procedureStyles.ProcedureBody}>
                            <JobSwmsV3EmergencyProcedureSectionRegistry
                              procedure={procedure}
                              swmsData={swmsData}
                              status={serverState.status || "DRAFT"}
                              procedureIndex={items.findIndex((item) => `${getProcedureKey(item)}` === `${procedureId}`)}
                            />
                          </div>
                        </Collapse.Panel>
                      );
                    })}
                  </Collapse>
                  {selectedPlanProcedures.length === 0 ? (
                    <div className={styles.PreStartEmptyState}>No procedures linked to this plan.</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </form>
      ) : null}
    </>
  );
}

export default reduxForm({ form: FORM_NAME, destroyOnUnmount: true, enableReinitialize: true })(JobSwmsV3EmergencyProceduresContainer);
